// @ts-strict-ignore
import { createReducer, createActions } from 'reduxsauce'
import isEqual from 'lodash/isEqual'
import Immutable, { type ImmutableObject } from 'seamless-immutable'

import {
  AvailabilitiesPerDate,
  AvailabilitiesExists,
  FetchAvailabilitiesSuccessPayload,
  FetchAvailabilitiesRequestPayload,
} from 'APP/Components/AppointmentBooking/utils/types'
import type { AppointmentBookingState } from 'APP/Store/AppointmentBooking/types'
import { PARTS_OF_DAY } from 'APP/Components/AppointmentBooking/constants'

import moment from 'moment'

/* ------------- Types and Action Creators ------------- */
const TODAY = moment()

/**
 * Internal date format for Redux state keys
 */
export const DATE_FORMAT = 'YYYY-MM-DD'

export const DEFAULT_TIMES = {
  [PARTS_OF_DAY.MORNING]: [],
  [PARTS_OF_DAY.AFTERNOON]: [],
  [PARTS_OF_DAY.EVENING]: [],
}

export const DEFAULT_AVAILABILITY: AvailabilitiesPerDate = {
  times: DEFAULT_TIMES,
  isLoading: false,
  hasAvailabilities: false,
}

const actionDefs = {
  getAvailableAppointments: ['payload'],
  getAvailableAppointmentsSuccess: ['payload'],
  getAvailableAppointmentsError: ['error', 'day'],

  getAvailabilitiesExists: ['payload'],
  getAvailabilitiesExistsSuccess: ['payload'],
  getAvailabilitiesExistsError: ['error'],

  selectDay: ['day'],
  selectAppointment: ['appointment'],

  createAppointment: ['payload'],
  createAppointmentSuccess: ['data'],
  createAppointmentError: ['error'],
  resetCreateAppointment: [],
  resetState: [],
} as const satisfies Record<string, string[]>

type TActions = typeof actionDefs
type TPayloads = {}

const { Types, Creators } = createActions<TPayloads, TActions>(actionDefs)

export const AppointmentBookingTypes = Types
export default Creators

type ImmutableAppointmentBookingState = ImmutableObject<AppointmentBookingState>

export const INITIAL_STATE: ImmutableAppointmentBookingState = Immutable<AppointmentBookingState>({
  availabilities: {
    [TODAY.format(DATE_FORMAT)]: DEFAULT_AVAILABILITY,
  },
  availabilitiesExists: {},
  selectedAppointment: null,
  selectedDay: TODAY.format(DATE_FORMAT),
  createAppointmentRequest: {
    isLoading: false,
    data: null,
    error: null,
  },
})

/* ------------- Reducers ------------- */
export const getAvailableAppointments = (
  state: ImmutableAppointmentBookingState,
  { payload }: { payload: FetchAvailabilitiesRequestPayload }
) => {
  const formattedDay = moment(payload.day).format(DATE_FORMAT)

  // If day is already in the store, leave it as is but update it's loading state.
  if (state.availabilities[formattedDay]) {
    return state.merge({
      availabilities: state.availabilities.merge({
        [formattedDay]: {
          ...state.availabilities[formattedDay],
          isLoading: true,
        },
      }),
    })
  }

  // Day is not captured yet, save it with default times.
  return state.merge({
    availabilities: state.availabilities.merge({
      [formattedDay]: {
        ...DEFAULT_AVAILABILITY,
        isLoading: true,
      },
    }),
  })
}

export const getAvailableAppointmentsSuccess = (
  state: ImmutableAppointmentBookingState,
  { payload }: { payload: FetchAvailabilitiesSuccessPayload }
) => {
  const formattedDay = moment(payload.day).format(DATE_FORMAT)
  const hasAvailabilities = Object.values(payload.times).some((list) => list.length > 0)

  // The exists map can say a day has times that its own fetch then returns empty. The fetch
  // wins: the day loses its dot and is no longer offered as the first available day.
  const availabilitiesExists = hasAvailabilities
    ? state.availabilitiesExists
    : state.availabilitiesExists.merge({ [formattedDay]: false })

  return state.merge({
    availabilities: state.availabilities.merge({
      [formattedDay]: {
        times: payload.times,
        isLoading: false,
        hasAvailabilities: hasAvailabilities,
      },
    }),
    availabilitiesExists,
  })
}

export const getAvailableAppointmentsError = (
  state: ImmutableAppointmentBookingState,
  { day }: { day: moment.Moment }
) => {
  const formattedDay = day.format(DATE_FORMAT)

  const payload: Partial<Record<string, AppointmentBookingState['availabilities'][string]>> = {
    [formattedDay]: {
      ...DEFAULT_AVAILABILITY,
      isLoading: false,
    },
  }

  return state.merge({
    availabilities: state.availabilities.merge(payload),
  })
}

export const getAvailabilitiesExistsSuccess = (
  state: ImmutableAppointmentBookingState,
  { payload }: { payload: AvailabilitiesExists }
) =>
  state.merge({
    availabilitiesExists: payload,
  })

export const getAvailabilitiesExistsError = (state: ImmutableAppointmentBookingState) =>
  state.merge({
    availabilitiesExists: {},
  })

export const selectAppointment = (
  state: ImmutableAppointmentBookingState,
  { appointment }: { appointment: AppointmentBookingState['selectedAppointment'] }
) =>
  state.merge({
    selectedAppointment: isEqual(state.selectedAppointment, appointment) ? null : appointment,
  })

export const selectDay = (state: ImmutableAppointmentBookingState, { day }: { day: string }) =>
  state.merge({
    selectedDay: day,
  })

export const createAppointment = (state: ImmutableAppointmentBookingState) =>
  state.merge({ createAppointmentRequest: { isLoading: true, data: null, error: null } })

export const createAppointmentSuccess = (
  state: ImmutableAppointmentBookingState,
  { data }: { data: AppointmentBookingState['createAppointmentRequest']['data'] }
) => state.merge({ createAppointmentRequest: { isLoading: false, data, error: null } })

export const createAppointmentError = (
  state: ImmutableAppointmentBookingState,
  { error }: { error: AppointmentBookingState['createAppointmentRequest']['error'] }
) => state.merge({ createAppointmentRequest: { isLoading: false, data: null, error } })

export const resetCreateAppointment = (state: ImmutableAppointmentBookingState) =>
  state.merge({
    createAppointmentRequest: { isLoading: false, data: null, error: null },
    selectedAppointment: null,
    availabilities: {},
    availabilitiesExists: {},
  })

export const resetState = () => INITIAL_STATE

/* ------------- Hookup Reducers To Types ------------- */
type TState = ImmutableAppointmentBookingState
export const reducer = createReducer<TPayloads, TActions, TState>(INITIAL_STATE, {
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_AVAILABLE_APPOINTMENTS]: getAvailableAppointments,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_AVAILABLE_APPOINTMENTS_SUCCESS]: getAvailableAppointmentsSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_AVAILABLE_APPOINTMENTS_ERROR]: getAvailableAppointmentsError,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_AVAILABILITIES_EXISTS_SUCCESS]: getAvailabilitiesExistsSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.GET_AVAILABILITIES_EXISTS_ERROR]: getAvailabilitiesExistsError,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SELECT_DAY]: selectDay,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.SELECT_APPOINTMENT]: selectAppointment,

  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CREATE_APPOINTMENT]: createAppointment,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CREATE_APPOINTMENT_SUCCESS]: createAppointmentSuccess,
  // @ts-expect-error TODO: write payload model for action & add it to TPayloads
  [Types.CREATE_APPOINTMENT_ERROR]: createAppointmentError,
  [Types.RESET_CREATE_APPOINTMENT]: resetCreateAppointment,
  [Types.RESET_STATE]: resetState,
})
